import React, { useState } from 'react';
import { Users, UserCheck, UserX, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import OnboardingRequestsTable from '@/components/admin/OnboardingRequestsTable';
import VerifiedClientsTable from '@/components/admin/VerifiedClientsTable';
import RejectedClientsTable from '@/components/admin/RejectedClientsTable';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface AdminClientManagementTabProps {
  onboardingRequests: any[];
  verifiedClients: any[];
  rejectedClients: any[];
  handleRequestAction: (request: any) => void;
  handleEditClient?: (client: any) => void;
  isProcessing: boolean;
  onRefresh: () => void;
  handleDeleteClient?: (request: any) => void;
  handleToggleActive?: (request: any) => void;
  handleSendInvitation?: (client: any) => void;
  handleSendBulkInvitations?: (clients: any[]) => void;
  onAddClient?: () => void;
  filterStatus?: string;
  onFilterChange?: (status: string) => void;
}

const AdminClientManagementTab: React.FC<AdminClientManagementTabProps> = ({
  onboardingRequests,
  verifiedClients,
  rejectedClients,
  handleRequestAction,
  handleEditClient,
  isProcessing,
  onRefresh,
  handleDeleteClient,
  handleToggleActive,
  handleSendInvitation,
  handleSendBulkInvitations,
  onAddClient,
  filterStatus = 'all',
  onFilterChange
}) => {
  const [activeSubTab, setActiveSubTab] = useState('requests');

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Client Management</h2>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCcw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-gray-100 mb-6">
          <TabsTrigger 
            value="requests" 
            className="flex items-center gap-2 data-[state=active]:bg-white"
          >
            <Users className="h-4 w-4" />
            End User Requests ({onboardingRequests.length})
          </TabsTrigger>
          <TabsTrigger 
            value="verified" 
            className="flex items-center gap-2 data-[state=active]:bg-white"
          >
            <UserCheck className="h-4 w-4" />
            Verified ({verifiedClients.length})
          </TabsTrigger>
          <TabsTrigger 
            value="rejected" 
            className="flex items-center gap-2 data-[state=active]:bg-white"
          >
            <UserX className="h-4 w-4" />
            Rejected ({rejectedClients.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          {onboardingRequests.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No pending requests found.</p>
            </div>
          ) : (
            <OnboardingRequestsTable 
              onboardingRequests={onboardingRequests}
              handleRequestAction={handleRequestAction}
              isProcessing={isProcessing}
              handleDeleteClient={handleDeleteClient}
            />
          )}
        </TabsContent>

        <TabsContent value="verified">
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2">
              {onFilterChange && (
                <Select value={filterStatus} onValueChange={onFilterChange}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {onAddClient && (
              <Button variant="default" size="sm" onClick={onAddClient}>
                Add Client
              </Button>
            )}
          </div>
          
          {verifiedClients.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No verified clients found.</p>
            </div>
          ) : (
            <VerifiedClientsTable 
              verifiedClients={verifiedClients}
              handleEditClient={handleEditClient}
              isProcessing={isProcessing}
              handleDeleteClient={handleDeleteClient}
              handleToggleActive={handleToggleActive}
              handleSendInvitation={handleSendInvitation}
            />
          )}
        </TabsContent>

        <TabsContent value="rejected">
          {rejectedClients.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No rejected clients found.</p>
            </div>
          ) : (
            <RejectedClientsTable 
              rejectedClients={rejectedClients}
              handleRequestAction={handleRequestAction}
              isProcessing={isProcessing}
              handleDeleteClient={handleDeleteClient}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminClientManagementTab;
